"use client";
import { useState, useEffect, useRef } from "react";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";

const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(startDate || "");
  const [localEndDate, setLocalEndDate] = useState(endDate || "");
  const pickerRef = useRef(null);

  useEffect(() => {
    setLocalStartDate(startDate || "");
    setLocalEndDate(endDate || "");
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleApply = () => {
    onChange({ startDate: localStartDate, endDate: localEndDate });
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalStartDate("");
    setLocalEndDate("");
    onChange({ startDate: null, endDate: null });
    setIsOpen(false);
  };

  const formatDisplayDate = () => {
    if (!localStartDate && !localEndDate) return "Select date range";
    if (localStartDate && !localEndDate) return `From ${localStartDate}`;
    if (!localStartDate && localEndDate) return `Until ${localEndDate}`;
    return `${localStartDate} - ${localEndDate}`;
  };

  return (
    <div className="relative" ref={pickerRef}>
      <div
        className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2 text-gray-500" />
          <span className="text-sm">{formatDisplayDate()}</span>
        </div>
        {(localStartDate || localEndDate) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <FaTimes className="text-gray-500" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50 p-3">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={localStartDate}
                onChange={(e) => setLocalStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={localEndDate}
                onChange={(e) => setLocalEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={handleClear}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                onClick={handleApply}
                className="px-3 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
